import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import Draggable, {type DraggableData, type DraggableEvent} from 'react-draggable';
import {X} from 'lucide-react';
import {getCustomContainer} from '../lib/utils';
import {cn} from '../lib/cn';
import {useCombinedRef, useIframeAriaRefFix} from '../hooks';

const DRAWER_CLOSE_THRESHOLD = 72;
const DRAWER_MIN_OVERLAY_OPACITY = 0.1;

function Drawer({...props}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="drawer" {...props} />;
}

function DrawerTrigger({...props}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({...props}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({...props}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="drawer-close" {...props} />;
}

const DrawerOverlayClasses =
  'fixed inset-0 z-50 bg-black/50 [animation-timing-function:cubic-bezier(0.32,0.72,0,1)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-500 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-500';

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="drawer-overlay"
    className={cn(DrawerOverlayClasses, className)}
    {...props}
  />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

function DrawerOffsetReset({onReset}: {onReset: () => void}) {
  React.useLayoutEffect(() => {
    onReset();
  }, [onReset]);
  return null;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    container?: HTMLElement;
    onClose?: () => void;
    showHandle?: boolean;
    withCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    lockScroll?: boolean;
    disableDrag?: boolean;
  }
>(
  (
    {
      className,
      children,
      container,
      onClose,
      showHandle = true,
      withCloseButton = true,
      closeOnOverlayClick = true,
      lockScroll: _lockScroll = true,
      disableDrag = false,
      ...props
    },
    ref,
  ) => {
    const finalContainer = container || getCustomContainer() || undefined;
    const nodeRef = React.useRef<HTMLDivElement>(null);
    const [dragOffsetY, setDragOffsetY] = React.useState(0);
    const [snapBack, setSnapBack] = React.useState(false);
    const overlayOpacity = Math.max(
      DRAWER_MIN_OVERLAY_OPACITY,
      1 - dragOffsetY / (DRAWER_CLOSE_THRESHOLD * 2),
    );

    const resetOffset = React.useCallback(() => {
      setSnapBack(false);
      setDragOffsetY(0);
    }, []);

    const handleStart = React.useCallback(() => {
      setSnapBack(false);
    }, []);

    const handleDrag = React.useCallback(
      (_event: DraggableEvent, data: DraggableData) => {
        setDragOffsetY(Math.max(0, data.y));
      },
      [],
    );

    const handleStop = React.useCallback(
      (_event: DraggableEvent, data: DraggableData) => {
        if (data.y > DRAWER_CLOSE_THRESHOLD) {
          setSnapBack(false);
          onClose?.();
          return;
        }
        setSnapBack(true);
        setDragOffsetY(0);
      },
      [onClose],
    );

    return (
      <DrawerPortal container={finalContainer}>
        <DrawerOverlay
          className={
            snapBack
              ? 'transition-opacity duration-500 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]'
              : undefined
          }
          style={{opacity: overlayOpacity}}
          onClick={closeOnOverlayClick ? onClose : undefined}
        />
        <DialogPrimitive.Content
          asChild
          ref={ref}
          onPointerDownOutside={event => {
            if (!closeOnOverlayClick) {
              event.preventDefault();
            }
          }}
          onInteractOutside={event => {
            if (!closeOnOverlayClick) {
              event.preventDefault();
            }
          }}
          {...props}
        >
          <div
            className={cn(
              'fixed inset-x-0 bottom-0 top-auto z-50 outline-none',
              '[animation-timing-function:cubic-bezier(0.32,0.72,0,1)]',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:duration-500',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=closed]:duration-500',
            )}
          >
            <DrawerOffsetReset onReset={resetOffset} />
            <Draggable
              axis="y"
              bounds={{top: 0}}
              handle="[data-drawer-handle]"
              nodeRef={nodeRef}
              disabled={disableDrag}
              onStart={handleStart}
              onDrag={handleDrag}
              onStop={handleStop}
              position={{x: 0, y: dragOffsetY}}
            >
              <div
                ref={nodeRef}
                className={cn(
                  'bg-[var(--modal-background)] relative flex h-[var(--chekin-drawer-height,auto)] max-h-[var(--chekin-drawer-max-height,calc(100vh-1rem))] w-full flex-col rounded-t-[var(--dialog-content-radius)] shadow-lg',
                  snapBack &&
                    'transition-transform duration-500 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]',
                  className,
                )}
              >
                {showHandle && (
                  <div
                    data-drawer-handle
                    className={cn(
                      'mx-auto flex h-8 w-24 touch-none items-center justify-center',
                      disableDrag
                        ? 'cursor-default'
                        : 'cursor-grab active:cursor-grabbing',
                    )}
                  >
                    <span className="block h-1.5 w-12 rounded-full bg-[#D9D7D3]" />
                  </div>
                )}
                {!showHandle && withCloseButton && (
                  <div className="flex flex-shrink-0 justify-end px-4 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Close"
                      className="rounded-full p-1 text-[var(--chekin-color-brand-navy)] hover:bg-[var(--chekin-color-surface-input-empty)]"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
              </div>
            </Draggable>
          </div>
        </DialogPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = DialogPrimitive.Content.displayName;

const DrawerHeader = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-2 px-5 pt-2 text-center', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-2 p-5', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({className, ...props}, ref) => {
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const combinedRef = useCombinedRef(titleRef, ref);

  useIframeAriaRefFix(titleRef);

  return (
    <DialogPrimitive.Title
      ref={combinedRef}
      data-slot="drawer-title"
      className={cn('text-lg font-semibold leading-none', className)}
      {...props}
    />
  );
});
DrawerTitle.displayName = DialogPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({className, ...props}, ref) => {
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);
  const combinedRef = useCombinedRef(descriptionRef, ref);

  useIframeAriaRefFix(descriptionRef);

  return (
    <DialogPrimitive.Description
      ref={combinedRef}
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
});
DrawerDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
